import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../../core/services';

/**
 * Data behind the Main Entities block: the project's configured entity-type
 * rows and the tabs grouping them, the entities of the active tab's types,
 * the pool of types that can still be added, and the mutations that edit all
 * of that.
 */
export function useMainEntitiesConfig(projectId: number) {
  const queryClient = useQueryClient();
  const monitoringEntityTypesApi = resources.projectMonitoringEntityTypesFor(projectId);
  const monitoringEntityTabsApi = resources.projectMonitoringEntityTabsFor(projectId);

  const entityTypeRowsQuery = useQuery({
    queryKey: ['project-monitoring-entity-types', projectId],
    queryFn: () => monitoringEntityTypesApi.list({ pageSize: 100, sort: 'order' }),
    enabled: Number.isFinite(projectId),
  });
  const entityTypeRows = entityTypeRowsQuery.data?.data ?? [];

  const entityTabsQuery = useQuery({
    queryKey: ['project-monitoring-entity-tabs', projectId],
    queryFn: () => monitoringEntityTabsApi.list({ pageSize: 100, sort: 'order' }),
    enabled: Number.isFinite(projectId),
  });
  const entityTabs = entityTabsQuery.data?.data ?? [];

  // Which Main Entities tab is showing (null = the implicit "General" tab).
  // Owned here rather than inside MainEntitiesSection so the entities query
  // below can be scoped to just the active tab's types.
  const [activeEntityTabId, setActiveEntityTabId] = useState<number | null>(null);
  // Falls back to General if the active tab was deleted out from under it.
  useEffect(() => {
    if (activeEntityTabId !== null && !entityTabs.some((t) => t.id === activeEntityTabId)) {
      setActiveEntityTabId(null);
    }
  }, [entityTabs, activeEntityTabId]);

  // The same entityType can be configured in more than one tab (separate
  // rows), so dedupe before it feeds the entities filter below. Scoped to the
  // active tab only — one request per tab rather than fetching every
  // configured type across the whole project up front.
  const configuredEntityTypesInActiveTab = Array.from(
    new Set(entityTypeRows.filter((r) => r.tabId === activeEntityTabId).map((r) => r.entityType)),
  );

  // Chips for the active tab's configured rows only.
  const mainEntitiesQuery = useQuery({
    queryKey: ['project-entities', projectId, activeEntityTabId, configuredEntityTypesInActiveTab],
    queryFn: () =>
      resources.entities.list({
        filter: { projectId: [projectId], entityType: configuredEntityTypesInActiveTab },
        pageSize: 2000,
      }),
    enabled: Number.isFinite(projectId) && configuredEntityTypesInActiveTab.length > 0,
  });

  // The "Add entity type" dropdown's pool: every entityType extracted for the
  // project so far, read straight off the table (not derived from the
  // possibly-filtered query above) so it stays correct as new documents get
  // summarized and the LLM invents types beyond whatever's configured today.
  const entityTypesQuery = useQuery({
    queryKey: ['project-entity-types', projectId],
    queryFn: () => resources.getProjectEntityTypes(projectId),
    enabled: Number.isFinite(projectId),
  });

  const invalidateEntityTypeRows = () =>
    queryClient.invalidateQueries({ queryKey: ['project-monitoring-entity-types', projectId] });

  const addEntityTypeRow = useMutation({
    mutationFn: ({ entityType, tabId }: { entityType: string; tabId: number | null }) => {
      const nextOrder =
        entityTypeRows.filter((r) => r.tabId === tabId).reduce((max, r) => Math.max(max, r.order), -1) + 1;
      return monitoringEntityTypesApi.create({ entityType, tabId, order: nextOrder });
    },
    onSuccess: invalidateEntityTypeRows,
  });

  const deleteEntityTypeRow = useMutation({
    mutationFn: (id: number) => monitoringEntityTypesApi.remove(id),
    onSuccess: invalidateEntityTypeRows,
  });

  const invalidateEntityTabs = () =>
    queryClient.invalidateQueries({ queryKey: ['project-monitoring-entity-tabs', projectId] });

  const addEntityTab = useMutation({
    mutationFn: ({ name, icon }: { name: string; icon: string | null }) => {
      const nextOrder = entityTabs.reduce((max, t) => Math.max(max, t.order), -1) + 1;
      return monitoringEntityTabsApi.create({ name, icon, order: nextOrder });
    },
    onSuccess: invalidateEntityTabs,
  });

  // A deleted tab's rows fall back to General server-side (tabId set to null
  // via ON DELETE SET NULL), so entity-type rows need a refetch too.
  const deleteEntityTab = useMutation({
    mutationFn: (id: number) => monitoringEntityTabsApi.remove(id),
    onSuccess: () => {
      invalidateEntityTabs();
      invalidateEntityTypeRows();
    },
  });

  return {
    entityTypeRows,
    entityTabs,
    entities: mainEntitiesQuery.data?.data ?? [],
    entitiesLoading: mainEntitiesQuery.isLoading,
    allEntityTypes: entityTypesQuery.data ?? [],
    isPending: entityTypeRowsQuery.isPending || entityTypesQuery.isPending || entityTabsQuery.isPending,
    activeTabId: activeEntityTabId,
    setActiveTabId: setActiveEntityTabId,
    addType: (entityType: string, tabId: number | null) => addEntityTypeRow.mutate({ entityType, tabId }),
    deleteType: (id: number) => deleteEntityTypeRow.mutate(id),
    addTab: (name: string, icon: string | null) => addEntityTab.mutateAsync({ name, icon }),
    deleteTab: (id: number) => deleteEntityTab.mutate(id),
  };
}
